import { useEffect, useState } from "react";
import axios from "axios";
import { Clock, ChevronDown, ChevronRight, Filter, Download, Trash2, RefreshCw } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SignalHistory = () => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [displayLimit, setDisplayLimit] = useState(25);
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [stats, setStats] = useState(null);
  const [filters, setFilters] = useState({
    source: null,
    signal_type: null,
    mtf_status: null
  });
  const [total, setTotal] = useState(0);

  const fetchHistory = async (limit = 100) => {
    try {
      setLoading(true);
      
      // Build query params
      const params = new URLSearchParams();
      params.append('limit', limit);
      if (filters.source) params.append('source', filters.source);
      if (filters.signal_type) params.append('signal_type', filters.signal_type);
      if (filters.mtf_status) params.append('mtf_status', filters.mtf_status);
      
      const response = await axios.get(`${API}/signal-generation-history?${params.toString()}`);
      setHistory(response.data.signals || []);
      setTotal(response.data.total || 0);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching history:", error);
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/signal-generation-history/stats`);
      setStats(response.data);
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  };

  useEffect(() => {
    fetchHistory(100);
    fetchStats();
  }, [filters]);

  const loadMore = () => {
    setDisplayLimit(prev => prev + 25);
  };

  const toggleRow = (index) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedRows(newExpanded);
  };

  const handleExport = async (format) => {
    try {
      const params = new URLSearchParams();
      params.append('format', format);
      if (filters.source) params.append('source', filters.source);
      if (filters.signal_type) params.append('signal_type', filters.signal_type);
      if (filters.mtf_status) params.append('mtf_status', filters.mtf_status);
      
      const response = await axios.get(`${API}/signal-generation-history/export?${params.toString()}`);
      
      if (format === 'csv') {
        // Download CSV
        const blob = new Blob([response.data], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'signal_generation_history.csv';
        a.click();
      } else {
        // Download JSON
        const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'signal_generation_history.json';
        a.click();
      }
    } catch (error) {
      console.error("Error exporting:", error);
    }
  };

  const displayedHistory = history.slice(0, displayLimit);

  const getSignalColor = (signal) => {
    switch (signal) {
      case "BUY": return "text-[#00FF94]";
      case "SELL": return "text-[#FF0055]";
      case "HOLD": return "text-[#FFD600]";
      default: return "text-[#888888]";
    }
  };

  const getSignalBg = (signal) => {
    switch (signal) {
      case "BUY": return "bg-[#00FF94]/10";
      case "SELL": return "bg-[#FF0055]/10";
      case "HOLD": return "bg-[#FFD600]/10";
      default: return "bg-[#888888]/10";
    }
  };

  const getMtfStatusColor = (status) => {
    switch (status) {
      case "EXECUTE": return "text-green-400 bg-green-400/10";
      case "WAIT": return "text-yellow-400 bg-yellow-400/10";
      case "SKIP": return "text-red-400 bg-red-400/10";
      default: return "text-gray-400 bg-gray-400/10";
    }
  };

  const getSqsColor = (score) => {
    if (score === null || score === undefined) return "text-gray-400";
    if (score >= 80) return "text-green-400";
    if (score >= 60) return "text-yellow-400";
    if (score >= 40) return "text-orange-400";
    return "text-red-400";
  };

  const getSqsBgColor = (score) => {
    if (score === null || score === undefined) return "bg-gray-400/10";
    if (score >= 80) return "bg-green-400/10";
    if (score >= 60) return "bg-yellow-400/10";
    if (score >= 40) return "bg-orange-400/10";
    return "bg-red-400/10";
  };

  const getSourceColor = (source) => {
    switch (source) {
      case "manual": return "text-blue-400 bg-blue-400/10";
      case "cron": return "text-purple-400 bg-purple-400/10";
      case "auto": return "text-cyan-400 bg-cyan-400/10";
      case "ui_individual": return "text-orange-400 bg-orange-400/10";
      default: return "text-gray-400 bg-gray-400/10";
    }
  };

  const formatTimestamp = (ts) => {
    if (!ts) return '-';
    return new Date(ts).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatPrice = (price) => {
    if (price === null || price === undefined) return '-';
    if (price < 1) return `$${price.toFixed(6)}`;
    if (price < 100) return `$${price.toFixed(4)}`;
    return `$${price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatPercent = (value) => {
    if (value === null || value === undefined) return '-';
    return `${(value * 100).toFixed(1)}%`;
  };

  if (loading) {
    return (
      <div className="glass-card rounded-xl p-6 h-64 flex items-center justify-center" data-testid="history-loading">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
          <p className="mt-2 text-xs text-muted-foreground">Loading signal generation history...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 animate-fade-in-up" data-testid="signal-history">
      {/* Stats Summary */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="glass-card rounded-lg p-3 border border-white/10">
            <p className="text-xs text-muted-foreground">Total Signals</p>
            <p className="text-xl font-bold text-data">{stats.total_signals}</p>
          </div>
          <div className="glass-card rounded-lg p-3 border border-white/10">
            <p className="text-xs text-muted-foreground">Executed</p>
            <p className="text-xl font-bold text-green-400">{stats.execution?.executed || 0}</p>
          </div>
          <div className="glass-card rounded-lg p-3 border border-white/10">
            <p className="text-xs text-muted-foreground">By Signal</p>
            <p className="text-xs">
              <span className="text-green-400">BUY: {stats.by_signal?.BUY || 0}</span> | 
              <span className="text-red-400 ml-1">SELL: {stats.by_signal?.SELL || 0}</span> | 
              <span className="text-yellow-400 ml-1">HOLD: {stats.by_signal?.HOLD || 0}</span>
            </p>
          </div>
          <div className="glass-card rounded-lg p-3 border border-white/10">
            <p className="text-xs text-muted-foreground">By MTF</p>
            <p className="text-xs">
              <span className="text-green-400">EXEC: {stats.by_mtf_status?.EXECUTE || 0}</span> | 
              <span className="text-yellow-400 ml-1">WAIT: {stats.by_mtf_status?.WAIT || 0}</span> | 
              <span className="text-red-400 ml-1">SKIP: {stats.by_mtf_status?.SKIP || 0}</span>
            </p>
          </div>
        </div>
      )}

      {/* Filters and Actions */}
      <div className="glass-card rounded-xl p-4 border border-white/10">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-muted-foreground" />
            <span className="text-sm text-muted-foreground">Filters:</span>
            
            <select
              value={filters.source || ''}
              onChange={(e) => setFilters({...filters, source: e.target.value || null})}
              className="bg-black/30 border border-white/10 rounded-lg px-2 py-1 text-xs"
            >
              <option value="">All Sources</option>
              <option value="manual">Manual</option>
              <option value="cron">Cron</option>
              <option value="auto">Auto</option>
              <option value="ui_individual">Individual</option>
            </select>
            
            <select
              value={filters.signal_type || ''}
              onChange={(e) => setFilters({...filters, signal_type: e.target.value || null})}
              className="bg-black/30 border border-white/10 rounded-lg px-2 py-1 text-xs"
            >
              <option value="">All Signals</option>
              <option value="BUY">BUY</option>
              <option value="SELL">SELL</option>
              <option value="HOLD">HOLD</option>
            </select>
            
            <select
              value={filters.mtf_status || ''}
              onChange={(e) => setFilters({...filters, mtf_status: e.target.value || null})}
              className="bg-black/30 border border-white/10 rounded-lg px-2 py-1 text-xs"
            >
              <option value="">All MTF</option>
              <option value="EXECUTE">EXECUTE</option>
              <option value="WAIT">WAIT</option>
              <option value="SKIP">SKIP</option>
            </select>
          </div>
          
          <div className="flex items-center gap-2">
            <button
              onClick={() => fetchHistory(100)}
              className="flex items-center gap-1 px-3 py-1 bg-white/10 hover:bg-white/20 rounded-lg text-xs transition-colors"
            >
              <RefreshCw className="w-3 h-3" />
              Refresh
            </button>
            <button
              onClick={() => handleExport('json')}
              className="flex items-center gap-1 px-3 py-1 bg-blue-500/20 hover:bg-blue-500/30 text-blue-400 rounded-lg text-xs transition-colors"
            >
              <Download className="w-3 h-3" />
              JSON
            </button>
            <button
              onClick={() => handleExport('csv')}
              className="flex items-center gap-1 px-3 py-1 bg-green-500/20 hover:bg-green-500/30 text-green-400 rounded-lg text-xs transition-colors"
            >
              <Download className="w-3 h-3" />
              CSV
            </button>
          </div>
        </div>
      </div>

      {/* History Table */}
      <div className="glass-card rounded-xl p-6 border-white/10">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2">
            <Clock className="w-5 h-5" />
            <h3 className="text-lg font-semibold heading-font">Signal Generation History</h3>
          </div>
          <span className="text-xs text-muted-foreground">
            Showing {displayedHistory.length} of {total} records
          </span>
        </div>

        <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
          <table className="w-full">
            <thead className="sticky top-0 bg-black/80 backdrop-blur-sm z-10">
              <tr className="border-b border-white/10 text-xs text-muted-foreground">
                <th className="text-left py-3 px-2 font-medium w-8"></th>
                <th className="text-left py-3 px-2 font-medium">Time</th>
                <th className="text-left py-3 px-2 font-medium">Coin</th>
                <th className="text-left py-3 px-2 font-medium">Signal</th>
                <th className="text-left py-3 px-2 font-medium">Confidence</th>
                <th className="text-left py-3 px-2 font-medium">SQS</th>
                <th className="text-left py-3 px-2 font-medium">Price</th>
                <th className="text-left py-3 px-2 font-medium">MTF</th>
                <th className="text-left py-3 px-2 font-medium">Source</th>
                <th className="text-left py-3 px-2 font-medium">Status</th>
              </tr>
            </thead>
            <tbody>
              {displayedHistory.length === 0 ? (
                <tr>
                  <td colSpan="10" className="text-center py-8 text-muted-foreground">
                    No signal generation history yet. Generate signals to see data here.
                  </td>
                </tr>
              ) : (
                displayedHistory.map((item, index) => (
                  <>
                    <tr 
                      key={item.id || index} 
                      className="border-b border-white/5 hover:bg-white/5 transition-colors cursor-pointer"
                      onClick={() => toggleRow(index)}
                      data-testid={`history-row-${index}`}
                    >
                      <td className="py-3 px-2">
                        {expandedRows.has(index) ? 
                          <ChevronDown className="w-4 h-4 text-muted-foreground" /> : 
                          <ChevronRight className="w-4 h-4 text-muted-foreground" />
                        }
                      </td>
                      <td className="py-3 px-2">
                        <span className="text-xs text-muted-foreground">{formatTimestamp(item.timestamp)}</span>
                      </td>
                      <td className="py-3 px-2">
                        <span className="font-semibold text-data">{item.coin}</span>
                      </td>
                      <td className="py-3 px-2">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-bold ${getSignalColor(item.signal)} ${getSignalBg(item.signal)}`}>
                          {item.signal}
                        </span>
                      </td>
                      <td className="py-3 px-2">
                        <span className="text-data font-semibold">{item.final_confidence}%</span>
                      </td>
                      <td className="py-3 px-2">
                        {item.sqs_score !== null && item.sqs_score !== undefined ? (
                          <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-bold ${getSqsColor(item.sqs_score)} ${getSqsBgColor(item.sqs_score)}`} title={item.sqs_label}>
                            {item.sqs_score}
                          </span>
                        ) : (
                          <span className="text-xs text-gray-500">-</span>
                        )}
                      </td>
                      <td className="py-3 px-2">
                        <span className="text-data text-xs">{formatPrice(item.price_at_signal)}</span>
                      </td>
                      <td className="py-3 px-2">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getMtfStatusColor(item.mtf_status)}`}>
                          {item.mtf_status || '-'}
                        </span>
                      </td>
                      <td className="py-3 px-2">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getSourceColor(item.source)}`}>
                          {item.source}
                        </span>
                      </td>
                      <td className="py-3 px-2">
                        <span className={`text-xs ${item.was_executed ? 'text-green-400' : 'text-gray-400'}`}>
                          {item.was_executed ? '✓ Executed' : item.execution_status?.replace('skipped_', '')}
                        </span>
                      </td>
                    </tr>
                    
                    {/* Expanded Details Row */}
                    {expandedRows.has(index) && (
                      <tr className="bg-white/5">
                        <td colSpan="10" className="p-4">
                          <div className="grid grid-cols-1 md:grid-cols-5 gap-4 text-xs">
                            {/* SQS Breakdown */}
                            <div className="space-y-2">
                              <h4 className="font-semibold text-cyan-400 flex items-center gap-1">
                                <span className="w-2 h-2 rounded-full bg-cyan-400"></span>
                                Signal Quality Score
                              </h4>
                              <div className="space-y-1 text-muted-foreground">
                                <p>Score: <span className={`font-bold ${getSqsColor(item.sqs_score)}`}>{item.sqs_score ?? '-'}</span></p>
                                <p>Label: <span className={`font-semibold ${getSqsColor(item.sqs_score)}`}>{item.sqs_label || '-'}</span></p>
                                {item.sqs_breakdown && (
                                  <>
                                    <p>Confidence Pts: <span className="text-white">{item.sqs_breakdown.confidence_pts?.toFixed(1) || '-'}/45</span></p>
                                    <p>MTF Pts: <span className="text-white">{item.sqs_breakdown.mtf_pts || '-'}/35</span></p>
                                    <p>Regime Pts: <span className="text-white">{item.sqs_breakdown.regime_pts?.toFixed(1) || '-'}/20</span></p>
                                  </>
                                )}
                                <p className="text-xs text-gray-500 mt-1">v{item.sqs_version || '-'}</p>
                              </div>
                            </div>
                            
                            {/* Confidence Breakdown */}
                            <div className="space-y-2">
                              <h4 className="font-semibold text-blue-400 flex items-center gap-1">
                                <span className="w-2 h-2 rounded-full bg-blue-400"></span>
                                Confidence Breakdown
                              </h4>
                              <div className="space-y-1 text-muted-foreground">
                                <p>Base Confidence: <span className="text-white">{item.base_confidence?.toFixed(1) || '-'}%</span></p>
                                <p>F&G Adjustment: <span className="text-white">{item.fear_greed_adjustment?.toFixed(1) || '0'}%</span></p>
                                <p>Regime Adjustment: <span className="text-white">{item.regime_adjustment?.toFixed(1) || '0'}%</span></p>
                                <p>Trend Penalty: <span className="text-white">{item.trend_penalty?.toFixed(1) || '0'}%</span></p>
                                <p>Final: <span className="text-green-400 font-semibold">{item.final_confidence}%</span></p>
                              </div>
                            </div>
                            
                            {/* AI Predictions */}
                            <div className="space-y-2">
                              <h4 className="font-semibold text-green-400 flex items-center gap-1">
                                <span className="w-2 h-2 rounded-full bg-green-400"></span>
                                AI Model Predictions
                              </h4>
                              <div className="space-y-1 text-muted-foreground">
                                <p>LSTM Price: <span className="text-white">{formatPrice(item.lstm_prediction)}</span></p>
                                <p>GB Price: <span className="text-white">{formatPrice(item.gb_prediction)}</span></p>
                                <p>Technical Score: <span className="text-white">{item.technical_score?.toFixed(3) || '-'}</span></p>
                                <p>AI Score: <span className="text-white">{item.ai_score?.toFixed(3) || '-'}</span></p>
                                <p>Fear & Greed: <span className="text-white">{item.fear_greed_value || '-'} ({item.fear_greed_classification || '-'})</span></p>
                              </div>
                            </div>
                            
                            {/* Market Regime */}
                            <div className="space-y-2">
                              <h4 className="font-semibold text-yellow-400 flex items-center gap-1">
                                <span className="w-2 h-2 rounded-full bg-yellow-400"></span>
                                Market Regime
                              </h4>
                              <div className="space-y-1 text-muted-foreground">
                                <p>Trend: <span className="text-white capitalize">{item.market_trend || '-'}</span></p>
                                <p>Vol. Level: <span className="text-white capitalize">{item.market_volatility || '-'}</span></p>
                                <p>Vol. Value: <span className="text-white">{item.market_volatility_value?.toFixed(2) || '-'}%</span></p>
                                <p>Vol. Z-Score: <span className="text-white">{item.market_volatility_z_score?.toFixed(2) || '-'}</span></p>
                                <p>Trend Strength: <span className="text-white">{formatPercent(item.market_trend_strength)}</span></p>
                              </div>
                            </div>
                            
                            {/* MTF Entry Timing */}
                            <div className="space-y-2">
                              <h4 className="font-semibold text-purple-400 flex items-center gap-1">
                                <span className="w-2 h-2 rounded-full bg-purple-400"></span>
                                MTF Entry Timing
                              </h4>
                              <div className="space-y-1 text-muted-foreground">
                                <p>Status: <span className={`font-semibold ${item.mtf_status === 'EXECUTE' ? 'text-green-400' : item.mtf_status === 'WAIT' ? 'text-yellow-400' : 'text-red-400'}`}>{item.mtf_status || '-'}</span></p>
                                <p>Hourly Aligned: <span className="text-white">{item.mtf_hourly_aligned ? '✓ Yes' : '✗ No'}</span></p>
                                <p>Hourly Conditions: <span className="text-white">{item.mtf_hourly_conditions_met ?? '-'}/3</span></p>
                                <p>5min Momentum: <span className="text-white capitalize">{item.mtf_5min_momentum || '-'}</span></p>
                                <p>5min RSI: <span className="text-white">{item.mtf_5min_rsi?.toFixed(1) || '-'}</span></p>
                                <p>Counter Score: <span className="text-white">{item.mtf_counter_score?.toFixed(1) ?? '-'}/3</span></p>
                                {item.mtf_wait_reason && (
                                  <p className="text-yellow-400 text-xs mt-1">Reason: {item.mtf_wait_reason}</p>
                                )}
                              </div>
                            </div>
                            
                            {/* Trade Details (if executed) */}
                            {item.was_executed && (
                              <div className="md:col-span-5 border-t border-white/10 pt-3 mt-2">
                                <h4 className="font-semibold text-teal-400 flex items-center gap-1 mb-2">
                                  <span className="w-2 h-2 rounded-full bg-teal-400"></span>
                                  Trade Details
                                </h4>
                                <div className="flex flex-wrap gap-6 text-muted-foreground">
                                  <p>Entry: <span className="text-white">{formatPrice(item.price_at_signal)}</span></p>
                                  <p>Target: <span className="text-green-400">{formatPrice(item.target_price)}</span></p>
                                  <p>Stop Loss: <span className="text-red-400">{formatPrice(item.stop_loss)}</span></p>
                                  <p>Position Size: <span className="text-white">${item.position_size?.toFixed(2) || '-'}</span></p>
                                </div>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </>
                ))
              )}
            </tbody>
          </table>
        </div>
        
        {displayLimit < history.length && (
          <div className="mt-4 text-center">
            <button
              onClick={loadMore}
              className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 rounded-lg text-sm transition-colors"
            >
              <ChevronDown className="w-4 h-4" />
              Load More ({history.length - displayLimit} remaining)
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default SignalHistory;
